import { memo, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export type ToolbarAction = 'bold' | 'italic' | 'link' | 'ul' | 'ol' | 'hr'

interface RichTextToolbarProps {
  // Which toggle buttons are pressed for the current selection
  pressed: Partial<Record<ToolbarAction, boolean>>
  linkOpen: boolean
  disabled?: boolean
  onAction: (action: ToolbarAction) => void
  linkControlsId: string
}

const svg = (children: React.ReactNode) => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false">
    {children}
  </svg>
)

const ICONS: Record<ToolbarAction, React.ReactNode> = {
  bold: svg(<path d="M7 5h6a3.5 3.5 0 0 1 0 7H7zM7 12h7a3.5 3.5 0 0 1 0 7H7z" />),
  italic: svg(<path d="M19 4h-9M14 20H5M15 4 9 20" />),
  link: svg(
    <>
      <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
      <path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />
    </>,
  ),
  ul: svg(<path d="M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01" />),
  ol: svg(
    <path d="M10 6h10M10 12h10M10 18h10M4 6h1v4M4 10h2M4 14.5a1.5 1.5 0 0 1 3 0c0 1-3 2-3 3.5h3" />,
  ),
  hr: svg(<path d="M3 12h18" />),
}

const ACTIONS: ToolbarAction[] = ['bold', 'italic', 'link', 'ul', 'ol', 'hr']
const TOGGLES: ToolbarAction[] = ['bold', 'italic', 'ul', 'ol']

// A WAI-ARIA toolbar: one tab stop, arrow keys (and Home/End) move between the buttons
export const RichTextToolbar = memo(
  ({ pressed, linkOpen, disabled, onAction, linkControlsId }: RichTextToolbarProps) => {
    const { t } = useTranslation('admin-mobifull')
    const [active, setActive] = useState(0)
    const buttons = useRef<Array<HTMLButtonElement | null>>([])

    const labelOf = (action: ToolbarAction): string =>
      ({
        bold: t('richText.bold'),
        italic: t('richText.italic'),
        link: t('richText.link'),
        ul: t('richText.bulletedList'),
        ol: t('richText.numberedList'),
        hr: t('richText.horizontalRule'),
      })[action]
    const titleOf = (action: ToolbarAction): string =>
      action === 'bold'
        ? t('richText.boldTitle')
        : action === 'italic'
          ? t('richText.italicTitle')
          : labelOf(action)

    const move = useCallback((to: number) => {
      setActive(to)
      buttons.current[to]?.focus()
    }, [])

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      const last = ACTIONS.length - 1
      if (e.key === 'ArrowRight') {
        move(active === last ? 0 : active + 1)
      } else if (e.key === 'ArrowLeft') {
        move(active === 0 ? last : active - 1)
      } else if (e.key === 'Home') {
        move(0)
      } else if (e.key === 'End') {
        move(last)
      } else {
        return
      }
      e.preventDefault()
    }

    return (
      <div
        className="rich-text__toolbar"
        role="toolbar"
        aria-label={t('richText.toolbar')}
        aria-orientation="horizontal"
        onKeyDown={handleKeyDown}>
        {ACTIONS.map((action, index) => (
          <button
            key={action}
            ref={el => {
              buttons.current[index] = el
            }}
            type="button"
            className="rich-text__button"
            aria-label={labelOf(action)}
            title={titleOf(action)}
            tabIndex={active === index ? 0 : -1}
            disabled={disabled}
            aria-pressed={TOGGLES.includes(action) ? !!pressed[action] : undefined}
            aria-expanded={action === 'link' ? linkOpen : undefined}
            aria-controls={action === 'link' && linkOpen ? linkControlsId : undefined}
            // Keep the selection in the editor when a button is clicked with the mouse
            onMouseDown={e => e.preventDefault()}
            onFocus={() => setActive(index)}
            onClick={() => onAction(action)}>
            {ICONS[action]}
          </button>
        ))}
      </div>
    )
  },
)

RichTextToolbar.displayName = 'RichTextToolbar'
